/**
 * Native details accordions with one-open behavior. The CSS
 * `grid-template-rows: 0fr -> 1fr` transition on `.faq__item::details-content`
 * (see sections/faq.liquid) drives the open/close motion — no measurement, no
 * WAAPI, survives content reflow. This file only sequences what CSS alone
 * cannot: closing the other open item, and delaying `open = false` until the
 * close transition actually finishes, so the content stays rendered (and
 * therefore animatable) for the full 420ms instead of vanishing instantly
 * under the native `<details>` default.
 *
 * `preventDefault()` on the summary click is still required for that delay:
 * letting the browser's native toggle run would flip the `open` attribute
 * synchronously as part of the same click, before any close animation could
 * play.
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const CLOSE_FALLBACK_MS = 500; // > the 420ms CSS transition; guards a dropped transitionend

/**
 * @param {HTMLDetailsElement} item
 * @param {string} summarySelector
 * @param {boolean} exclusive  true for the question rows, which close each
 *   other; false for the "Read more" group, which is a disclosure and has no
 *   siblings to close.
 */
function bindFaqItem(item, summarySelector = '.faq__question', exclusive = true) {
  if (item.dataset.faqReady !== undefined) return;
  item.dataset.faqReady = '';

  const summary = /** @type {HTMLElement | null} */ (item.querySelector(summarySelector));
  if (!summary) return;

  /** @type {number | undefined} */
  let closeTimer;

  /**
   * NOT `{ once: true }`. `once` unbinds on the FIRST event the listener
   * receives, which is not the same as the first event it accepts — and
   * `transitionend` bubbles, so the disclosure icon's own transform/opacity
   * transitions on `.faq__icon::after` reach `.faq__item` too. Measured in
   * WebKit 26.5: closing an item dispatches `opacity` and `transform` from
   * descendants alongside `grid-template-rows`, and the ordering is not the
   * same as Chromium's. A `once` listener guarded by a property test can
   * therefore be spent on a bubbled event it rejects, after which the real
   * grid-template-rows end never arrives and the close falls through to the
   * 500ms timer — visibly a stall, then a snap. Bind once, unbind explicitly.
   *
   * The grid transition itself lives on `::details-content` in browsers that
   * have it (see sections/faq.liquid). Transitions on a pseudo-element
   * dispatch with `target` set to the ORIGINATING element, so `item` is still
   * the target and the containment test still holds.
   *
   * @param {TransitionEvent} event
   */
  const onTransitionEnd = (event) => {
    // `event.target === item`, NOT `item.contains(target)`. The looser test was
    // safe while only question rows were bound, because none of them nested.
    // `.faq__more` CONTAINS question rows, and their own grid-template-rows
    // transitions bubble — so a row closing inside the group would finish the
    // GROUP's close and snap it shut. A transition on `::details-content`
    // dispatches with target set to the originating element, so the strict
    // test still matches the pseudo-element path this relies on.
    if (event.propertyName.indexOf('grid-template') === 0 && event.target === item) finishClose();
  };

  const finishClose = () => {
    window.clearTimeout(closeTimer);
    closeTimer = undefined;
    item.removeEventListener('transitionend', onTransitionEnd);
    item.open = false;
    item.classList.remove('is-closing');
  };

  const beginClose = () => {
    if (!item.open || item.classList.contains('is-closing')) return;

    if (reduceMotion.matches) {
      finishClose();
      return;
    }

    item.classList.add('is-closing');
    closeTimer = window.setTimeout(finishClose, CLOSE_FALLBACK_MS);
    item.addEventListener('transitionend', onTransitionEnd);
  };

  item.addEventListener('faq:close', beginClose);

  summary.addEventListener('click', (event) => {
    event.preventDefault();

    if (item.classList.contains('is-closing')) return;

    if (item.open) {
      beginClose();
      return;
    }

    if (exclusive) {
      document.querySelectorAll('.faq__item[open]').forEach((other) => {
        if (other !== item) other.dispatchEvent(new CustomEvent('faq:close'));
      });
    }
    item.open = true;
  });
}

/** @param {Document | Element} root */
function initFaqAccordion(root = document) {
  root.querySelectorAll('.faq__item').forEach((item) => bindFaqItem(/** @type {HTMLDetailsElement} */ (item)));
  root.querySelectorAll('.faq__more').forEach((group) =>
    bindFaqItem(/** @type {HTMLDetailsElement} */ (group), '.faq__more-toggle', false));
}

initFaqAccordion();

document.addEventListener('shopify:section:load', (event) => initFaqAccordion(/** @type {Element} */ (event.target)));

export {};
